import { Mic, Keyboard } from 'lucide-react';
import { UI_TEXT } from '../../config/ui-text';
import { useVoiceCapture, type VoiceOrTextTranscribeContext } from './useVoiceCapture';
import { MicButtonVisual } from './MicButtonVisual';

export type { VoiceOrTextTranscribeContext } from './useVoiceCapture';

const MSG = UI_TEXT.HUMAN_SURVEY.SESSION.MESSAGES;

export interface VoiceOrTextInputProps {
  /** A non-string prior answer is never a real answer to THIS slot. */
  value: string;
  onChange: (value: string) => void;

  /** CONTROLLED, not local state — survives this component unmounting/remounting across differently
   *  shaped question layouts. See ADR-0002 §4. */
  showTextInput: boolean;
  onShowTextInputChange: (showTextInput: boolean, forcedBySurvey?: boolean) => void;

  /** Sticky, one-way "voice is broken" kill switch (frontend#659). Controlled for the same reason
   *  as `showTextInput`; each caller scopes it. See ADR-0002 §4. */
  transcriptionUnavailable: boolean;
  onTranscriptionUnavailable: () => void;

  /** Author overrides. Never both true — QuestionCard already prevents that. */
  disableVoiceInput?: boolean;
  disableTextInput?: boolean;

  /** Identifies which capture this belongs to; a capture from a PREVIOUS slotKey is discarded
   *  rather than written into whatever is on screen when it resolves. See ADR-0002 §4. */
  slotKey: string;

  transcribeContext?: VoiceOrTextTranscribeContext;

  placeholder?: string;

  /** SurveySession feeds this into the idle-timeout heartbeat, so talking never reads as absence. */
  onRecordingChange?: (isRecording: boolean) => void;

  /** Caller decides where to show it — SurveySession's banner vs. AiDynamicSectionRunner's inline
   *  nudge, since a dynamic section must never look like a hard error (spec-01). */
  onError?: (message: string) => void;

  /** Supplied only by the caller that submits on stop instead of on Next — see the option of the
   *  same name on `useVoiceCapture`, which carries the reasoning. */
  onSpokenAnswerCaptured?: (clip: Blob) => void;

  /** The same idea one moment later, for a caller whose next step consumes the words rather than
   *  patching them in afterwards — see `useVoiceCapture`, which carries the reasoning. */
  onSpokenAnswerTranscribed?: (fullValue: string) => void;

  /** A save for this question is still in flight, so a take started now would have its own save
   *  dropped: the idle mic will not start one, and says why. See ADR-0011. */
  saveInFlight?: boolean;
}

/**
 * The voice-or-type input every question type renders, extracted so `AiDynamicSectionRunner`'s
 * `open_text` turns get the same experience instead of a bare textarea. Mic lifecycle lives in
 * `useVoiceCapture`; this is the presentational half. `showTextInput`/`transcriptionUnavailable`
 * are deliberately NOT local state — see their docblocks above and ADR-0002 §4.
 */
export function VoiceOrTextInput({
  value,
  onChange,
  showTextInput,
  onShowTextInputChange,
  transcriptionUnavailable,
  onTranscriptionUnavailable,
  disableVoiceInput = false,
  disableTextInput = false,
  slotKey,
  transcribeContext,
  placeholder = UI_TEXT.HUMAN_SURVEY.SESSION.PLACEHOLDERS.OPEN_ENDED,
  onRecordingChange,
  onError,
  onSpokenAnswerCaptured,
  onSpokenAnswerTranscribed,
  saveInFlight = false,
}: VoiceOrTextInputProps) {
  const {
    isRecording,
    isTranscribing,
    micStatus,
    audioLevels,
    noSignal,
    startRecording,
    stopRecording,
  } = useVoiceCapture({
    value,
    onChange,
    onShowTextInputChange,
    onTranscriptionUnavailable,
    slotKey,
    transcribeContext,
    onRecordingChange,
    onError,
    onSpokenAnswerCaptured,
    onSpokenAnswerTranscribed,
  });

  // Also true once transcription has failed - the mic is pointless if nothing can transcribe it.
  const voiceDisabled = disableVoiceInput || transcriptionUnavailable;
  const textDisabled = disableTextInput;
  const startBlocked = saveInFlight && micStatus === 'idle' && !isTranscribing;

  if (!showTextInput) {
    return (
      <div className="text-center space-y-6">
        <div className="flex flex-col items-center space-y-4">
          <MicButtonVisual
            isRecording={isRecording}
            isTranscribing={isTranscribing}
            audioLevels={audioLevels}
            onToggle={micStatus !== 'idle' ? stopRecording : startRecording}
            startBlockedReason={startBlocked ? MSG.SAVING_LAST_ANSWER : undefined}
          />

          <div className="text-center">
            <p className="text-lg font-medium text-gray-900 mb-2">
              {startBlocked
                ? MSG.SAVING_LAST_ANSWER
                : micStatus === 'warming'
                  ? MSG.MIC_WARMING
                  : micStatus === 'listening'
                    ? noSignal
                      ? MSG.NO_SIGNAL_YET
                      : MSG.LISTENING
                    : isTranscribing
                      ? MSG.CONVERTING_SPEECH
                      : MSG.TAP_TO_SPEAK}
            </p>
            {micStatus === 'idle' && !isTranscribing && (
              <p className="text-sm text-gray-500">{MSG.VOICE_CONVERTED}</p>
            )}
            {(isRecording || isTranscribing) && (
              <div className="flex items-center justify-center space-x-2 text-sm text-gray-600">
                {isRecording && (
                  <>
                    <div className="w-3 h-3 bg-red-500 rounded-full animate-pulse" />
                    <span>{MSG.RECORDING_TAP_AGAIN}</span>
                  </>
                )}
                {isTranscribing && (
                  <>
                    <div className="w-3 h-3 border-2 border-gray-400 border-t-transparent rounded-full animate-spin" />
                    <span>{MSG.PROCESSING_SPEECH}</span>
                  </>
                )}
              </div>
            )}
          </div>
        </div>

        {value && (
          <div className="bg-gray-50 border border-gray-200 rounded-lg p-4">
            <p className="text-sm text-gray-600 mb-2">{MSG.YOUR_RESPONSE}</p>
            <p className="text-gray-900">{value}</p>
          </div>
        )}

        {!textDisabled && (
          <button
            type="button"
            onClick={() => onShowTextInputChange(true)}
            className="inline-flex items-center space-x-1 text-xs text-gray-400 hover:text-gray-600 transition-colors mt-4"
          >
            <Keyboard className="w-3 h-3" />
            <span>{MSG.SWITCH_TO_TEXT}</span>
          </button>
        )}
      </div>
    );
  }

  return (
    <div className="space-y-4">
      <div className="flex items-center justify-between">
        <h3 className="text-lg font-medium text-gray-900">{MSG.TYPE_YOUR_ANSWER}</h3>
        {!voiceDisabled && (
          <button
            type="button"
            onClick={() => onShowTextInputChange(false)}
            className="inline-flex items-center space-x-2 text-sm text-blue-600 hover:text-blue-700 transition-colors"
          >
            <Mic className="w-4 h-4" />
            <span>{MSG.SWITCH_TO_VOICE}</span>
          </button>
        )}
      </div>
      <textarea
        value={value}
        onChange={(e) => onChange(e.target.value)}
        placeholder={placeholder}
        className="w-full px-4 py-3 border border-gray-300 rounded-lg resize-none focus:ring-2 focus:ring-blue-500 focus:border-transparent"
        rows={4}
        autoFocus
      />
    </div>
  );
}
